import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Clock, 
  Zap,
  Shield,
  ChevronRight,
  FileText,
  Settings
} from 'lucide-react';

interface Task {
  step: number;
  description: string;
  tool?: string;
  dependencies?: number[];
  risk_level?: 'low' | 'medium' | 'high';
  estimated_time_ms?: number;
}

interface ExecutionPlan {
  id: string;
  plan_description: string;
  tasks: Task[];
  requires_approval: boolean;
  confidence_score: number;
  estimated_total_time_ms: number;
  created_at: string;
}

interface PlanApprovalProps {
  plan: ExecutionPlan;
  onApprove: () => void;
  onReject: () => void;
  onModify?: () => void;
  isLoading?: boolean;
}

export const PlanApproval: React.FC<PlanApprovalProps> = ({
  plan,
  onApprove,
  onReject,
  onModify,
  isLoading = false
}) => {
  const getRiskColor = (risk?: string) => {
    switch (risk) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getRiskIcon = (risk?: string) => {
    switch (risk) {
      case 'high': return <AlertCircle className="h-4 w-4" />;
      case 'medium': return <Shield className="h-4 w-4" />;
      case 'low': return <CheckCircle2 className="h-4 w-4" />;
      default: return <Zap className="h-4 w-4" />;
    }
  };

  const formatTime = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Execution Plan Approval Required
            </CardTitle>
            <CardDescription className="mt-1">
              Review the proposed actions before execution
            </CardDescription>
          </div>
          <Badge variant={plan.requires_approval ? "destructive" : "secondary"}>
            {plan.requires_approval ? "Approval Required" : "Auto-Execute"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Plan Overview */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <h3 className="font-semibold text-sm">Plan Overview</h3>
          <p className="text-sm text-muted-foreground">{plan.plan_description}</p>
          
          <div className="flex gap-4 mt-3 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>Est. Time: {formatTime(plan.estimated_total_time_ms)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <span>Confidence: {(plan.confidence_score * 100).toFixed(0)}%</span>
            </div>
            <div className="flex items-center gap-1">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span>{plan.tasks.length} Steps</span>
            </div>
          </div>
        </div>

        {/* Task List */}
        <div>
          <h3 className="font-semibold text-sm mb-3">Execution Steps</h3>
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {plan.tasks.map((task, index) => (
                <div
                  key={task.step}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                    {task.step}
                  </div>
                  
                  <div className="flex-1 space-y-1">
                    <div className="flex items-start justify-between">
                      <p className="text-sm font-medium">{task.description}</p>
                      <div className={`flex items-center gap-1 ${getRiskColor(task.risk_level)}`}>
                        {getRiskIcon(task.risk_level)}
                        <span className="text-xs capitalize">{task.risk_level || 'unknown'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {task.tool && (
                        <span className="flex items-center gap-1">
                          <ChevronRight className="h-3 w-3" />
                          Tool: {task.tool}
                        </span>
                      )}
                      {task.estimated_time_ms && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(task.estimated_time_ms)}
                        </span>
                      )}
                      {task.dependencies && task.dependencies.length > 0 && (
                        <span className="flex items-center gap-1">
                          Depends on: {task.dependencies.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Warning for high-risk operations */}
        {plan.tasks.some(t => t.risk_level === 'high') && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-sm">
              This plan contains high-risk operations that may affect your data or system configuration.
              Please review carefully before approving.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>

      <CardFooter className="flex justify-between gap-3">
        <div className="flex gap-2">
          <Button
            variant="default"
            onClick={onApprove}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Approve & Execute
          </Button>
          
          <Button
            variant="destructive"
            onClick={onReject}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
        </div>
        
        {onModify && (
          <Button
            variant="outline"
            onClick={onModify}
            disabled={isLoading}
          >
            <Settings className="h-4 w-4 mr-2" />
            Modify Plan
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};